export interface UserInitialsInput {
    firstName?: string | null
    lastName?: string | null
    email?: string | null
    role?: string | null
}

/**
 * Derives a 2-letter uppercase initial string from user details.
 *
 * Fallback priority:
 * 1. First initial + Last initial (if both firstName and lastName are present)
 * 2. First 2 characters of firstName (if firstName has length >= 2)
 * 3. Single-character firstName + 'U' (if firstName has length === 1)
 * 4. First 2 alphanumeric characters of email (if email is present)
 * 5. First 2 characters of role (if role has length >= 2)
 * 6. Provided fallback (default: 'US')
 *
 * @param input - Object containing any combination of firstName, lastName, email, and role
 * @param fallback - Fallback string if no initials could be determined (default: 'US')
 * @returns 2-letter uppercase initials
 */
export function getUserInitials(
    input?: UserInitialsInput | null,
    fallback = 'US',
): string {
    if (!input) {
        return fallback
    }

    const first = input.firstName?.trim() ?? ''
    const last = input.lastName?.trim() ?? ''

    if (first && last) {
        return (first.charAt(0) + last.charAt(0)).toUpperCase()
    }

    if (first.length >= 2) {
        return first.slice(0, 2).toUpperCase()
    }

    if (first.length === 1) {
        return (first + 'U').toUpperCase()
    }

    const email = input.email?.trim() ?? ''
    if (email) {
        const clean = email.replace(/[^a-zA-Z0-9]/g, '')
        if (clean.length >= 2) {
            return clean.slice(0, 2).toUpperCase()
        }
        if (email.length >= 2) {
            return email.slice(0, 2).toUpperCase()
        }
    }

    const role = input.role?.trim() ?? ''
    if (role.length >= 2) {
        return role.slice(0, 2).toUpperCase()
    }

    return fallback
}
